import React, { useState } from 'react';
import { ArrowLeft, CalendarDays, Edit3, Euro, FileCheck, Hash, List, Plus, Search, Send } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { nextInvoiceNumber } from '../../../../../services/InvoicesService';
import QuoteForm from '../forms/QuoteForm';
import { InvoicePrefill, formatDate } from '../forms/FormParts';
import { PRESET_QUOTES, Quote, QuoteStatus, STORAGE_KEYS, formatEuro } from './financeMasters';
import { useStoredList, writeStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

const SERIES = 'PRE-2026';

const STATUS_BADGE: Record<QuoteStatus, string> = {
  Pendiente: 'pending',
  Aceptado: 'approved',
  Rechazado: 'cancelled'
};

interface FinanceQuotesViewProps {
  privacyMode: boolean;
  onNavigate: (groupId: string, subScreenId?: string) => void;
  onConvertToInvoice?: (prefill: InvoicePrefill) => void;
  // Abre directamente el formulario de un presupuesto nuevo
  startNew?: boolean;
}

export const FinanceQuotesView = ({ privacyMode, onNavigate, onConvertToInvoice, startNew = false }: FinanceQuotesViewProps) => {
  const [quotes, setQuotes] = useStoredList<Quote>(STORAGE_KEYS.quotes, PRESET_QUOTES);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  // Presupuesto abierto en el formulario ('new' si es nuevo)
  const [formQuote, setFormQuote] = useState<Quote | 'new' | null>(startNew ? 'new' : null);

  const closePanel = () => setSelectedQuote(null);

  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedQuote));

  const handleSave = (quote: Quote) => {
    const exists = quotes.some(q => q.number === quote.number);
    const next = exists ? quotes.map(q => (q.number === quote.number ? quote : q)) : [quote, ...quotes];
    setQuotes(next);
    writeStoredList(STORAGE_KEYS.quotes, next);
    setSelectedQuote(startNew ? null : quote);
  };

  const handleCloseForm = () => {
    if (startNew) onNavigate('deudores', 'presupuestos');
    else setFormQuote(null);
  };

  const handleEdit = (quote: Quote) => {
    setSelectedQuote(null);
    setFormQuote(quote);
  };

  if (formQuote) {
    const editing = formQuote === 'new' ? undefined : formQuote;
    return (
      <div className="fin-master">
        {!startNew && (
          <div className="module-toolbar">
            <button type="button" className="qa-back-btn" onClick={() => setFormQuote(null)}>
              <ArrowLeft size={14} /> Volver a presupuestos
            </button>
          </div>
        )}
        <QuoteForm
          privacyMode={privacyMode}
          quote={editing}
          nextNumber={nextInvoiceNumber(quotes, `${SERIES}-`)}
          onSave={handleSave}
          onConvert={(prefill) => onConvertToInvoice && onConvertToInvoice(prefill)}
          onClose={handleCloseForm}
        />
      </div>
    );
  }

  const query = searchQuery.toLowerCase();
  const filteredQuotes = quotes.filter(q =>
    q.number.toLowerCase().includes(query) ||
    q.clientName.toLowerCase().includes(query) ||
    q.clientCif.toLowerCase().includes(query) ||
    q.status.toLowerCase().includes(query)
  );

  const columns: ColumnDef<Quote>[] = [
    {
      id: 'number',
      header: 'Nº PRESUPUESTO',
      render: (q) => <span className="fin-master__code">{q.number}</span>
    },
    {
      id: 'client',
      header: 'CLIENTE',
      render: (q) => (
        <div>
          <div className="fin-master__name">{q.clientName}</div>
          {q.clientCif && <div className="fin-master__sub">CIF: {q.clientCif}</div>}
        </div>
      )
    },
    {
      id: 'date',
      header: 'FECHA',
      render: (q) => <span className="fin-master__text">{formatDate(q.date)}</span>
    },
    {
      id: 'validUntil',
      header: 'VÁLIDO HASTA',
      render: (q) => <span className="fin-master__text">{formatDate(q.validUntil)}</span>
    },
    {
      id: 'total',
      header: 'TOTAL',
      align: 'right',
      render: (q) => <span className="fin-master__amount">{formatEuro(q.total, privacyMode)}</span>
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (q) => (
        <div>
          <span className={`module-badge ${STATUS_BADGE[q.status]}`}>{q.status}</span>
          {q.sentAt && <div className="fin-master__sub">Enviado {formatDate(q.sentAt)}</div>}
        </div>
      )
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR Nº, CLIENTE, CIF O ESTADO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={() => onNavigate('deudores', 'crear_presupuesto')} className="module-create-btn">
          <Plus size={14} /> Nuevo_Presupuesto
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedQuote)}>
        <DataTable
          data={filteredQuotes}
          columns={columns}
          title={<><FileCheck size={14} className="module-panel-title-icon" /> Presupuestos_A_Clientes</>}
          subtitle={`TOTAL: ${filteredQuotes.length}`}
          getRowId={(q) => q.number}
          onRowClick={(q) => setSelectedQuote(q)}
          selectedRowId={selectedQuote?.number}
          emptyText="No se encontraron presupuestos."
          actionsHeader="ACCIONES"
          actionsColumn={(q) => (
            <RowActionsMenu
              actions={[
                viewAction(() => setSelectedQuote(q)),
                editAction(() => handleEdit(q))
              ]}
            />
          )}
        />

        {selectedQuote && (
          <DetailPanel
            expandable
            title="Ficha_De_Presupuesto"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEdit(selectedQuote)} />
            }
            cards={
              <DetailCard icon={List} title={`Líneas (${selectedQuote.lines.length})`}>
                <div className="fin-master__attachments">
                  {selectedQuote.lines.map(l => (
                    <div key={l.id} className="fin-master__attachment">
                      <span>{l.description}</span>
                      <span className="fin-master__sub">
                        {l.quantity} × {formatEuro(l.price, privacyMode)} • IVA {l.vat}%
                      </span>
                    </div>
                  ))}
                </div>
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={FileCheck}
              reference={selectedQuote.number}
              name={selectedQuote.clientName}
              subtitle={<span className={`module-badge ${STATUS_BADGE[selectedQuote.status]}`}>{selectedQuote.status}</span>}
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="CIF">{selectedQuote.clientCif || '—'}</DetailField>
              <DetailField icon={CalendarDays} label="FECHA">{formatDate(selectedQuote.date)}</DetailField>
              <DetailField icon={CalendarDays} label="VÁLIDO_HASTA">{formatDate(selectedQuote.validUntil)}</DetailField>
              <DetailField icon={Send} label="ENVIADO">{selectedQuote.sentAt ? formatDate(selectedQuote.sentAt) : 'No'}</DetailField>
              <DetailField icon={Euro} label="SUBTOTAL">{formatEuro(selectedQuote.subtotal, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="IVA">{formatEuro(selectedQuote.vat, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="TOTAL">{formatEuro(selectedQuote.total, privacyMode)}</DetailField>
            </DetailFieldList>

            {selectedQuote.notes && <DetailNotes title="Notas">{selectedQuote.notes}</DetailNotes>}
          </DetailPanel>
        )}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceQuotesView;
